
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, Settings } from 'lucide-react';
import { useBoardContext } from '@/contexts/BoardContext';
import { TagManagementDialog } from './TagManagementDialog';

interface TagPickerProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

export const TagPicker = ({ selectedTags, onTagsChange }: TagPickerProps) => {
  const { tagDefinitions } = useBoardContext();

  const handleTagSelect = (tagId: string) => {
    if (!selectedTags.includes(tagId)) {
      onTagsChange([...selectedTags, tagId]);
    }
  };

  const handleTagRemove = (tagId: string) => {
    onTagsChange(selectedTags.filter(id => id !== tagId));
  };

  const getTagById = (tagId: string) => {
    return tagDefinitions?.find(tag => tag.id === tagId);
  };

  return (
    <div className="space-y-3">
      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map((tagId) => {
            const tag = getTagById(tagId);
            if (!tag) return null;
            
            return (
              <Badge
                key={tagId}
                style={{ backgroundColor: tag.color, color: 'white' }}
                className="flex items-center gap-1"
              >
                {tag.name}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-white/20"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleTagRemove(tagId);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Available Tags */}
      <div className="flex flex-wrap gap-2">
        {tagDefinitions?.filter(tag => !selectedTags.includes(tag.id)).map((tag) => (
          <Badge
            key={tag.id}
            variant="outline"
            className="cursor-pointer hover:bg-accent"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleTagSelect(tag.id);
            }}
          >
            <div
              className="w-2 h-2 rounded-full mr-1"
              style={{ backgroundColor: tag.color }}
            />
            {tag.name}
          </Badge>
        ))}
      </div>

      {/* Manage Tags Button */}
      <div className="flex justify-between items-center pt-2 border-t">
        <span className="text-sm text-muted-foreground">
          {tagDefinitions?.length || 0} tag{tagDefinitions?.length !== 1 ? 's' : ''} available
        </span>
        <TagManagementDialog
          onTagSelected={handleTagSelect}
          selectionMode={true}
          selectedTags={selectedTags}
        >
          <Button variant="ghost" size="sm">
            <Settings className="h-4 w-4 mr-2" />
            Manage Tags
          </Button>
        </TagManagementDialog>
      </div>

      {tagDefinitions?.length === 0 && (
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground mb-2">
            No tags available yet.
          </p>
          <TagManagementDialog
            onTagSelected={handleTagSelect}
            selectionMode={true}
            selectedTags={selectedTags}
          >
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4 mr-2" />
              Create First Tag
            </Button>
          </TagManagementDialog>
        </div>
      )}
    </div>
  );
};
